import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge, Modal, ModalHeader,
  ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert, Spinner,
  Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';
import {
  Plus, RefreshCw, Edit, Trash2, User, Users as UsersIcon,
  Crown, Shield, Search, Eye, Key
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API from '../utils/api';

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    company: '',
    role: 'user',
    manager_id: '',
    is_active: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    per_page: 50,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    manager_id: ''
  });

  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';

  useEffect(() => {
    loadUsers();
    if (isAdmin) {
      loadManagers();
    }
  }, [filters.search, filters.role, filters.manager_id, pagination.page]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Construct API endpoint based on role
      const endpoint = isAdmin ? '/admin/users' : '/users';
      const params = new URLSearchParams({
        page: pagination.page,
        per_page: pagination.per_page,
        ...(filters.search && { search: filters.search }),
        ...(filters.role && { role: filters.role }),
        ...(filters.manager_id && { manager_id: filters.manager_id })
      });

      const response = await API.get(`${endpoint}?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const response = await API.get('/admin/managers');
      setManagers(response.data.managers);
    } catch (error) {
      console.error('Failed to load managers:', error);
    }
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setEditMode(false);
    setFormData({
      email: '',
      username: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      company: '',
      role: isAdmin ? 'user' : 'user', // Managers can only create users
      manager_id: isManager ? '' : '',
      is_active: true
    });
    setError('');
    setSuccess('');
    setModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditMode(true);
    setFormData({
      email: user.email || '',
      username: user.username || '',
      password: '', // Never pre-fill password
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      company: user.company || '',
      role: user.role || 'user',
      manager_id: user.manager_id || '',
      is_active: user.is_active !== false
    });
    setError('');
    setSuccess('');
    setModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const endpoint = isAdmin ? `/admin/users/${userId}` : `/users/${userId}`;
      await API.delete(endpoint);
      setSuccess('User deleted successfully');
      loadUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editMode) {
        // Update user
        const endpoint = isAdmin ? `/admin/users/${selectedUser.id}` : `/users/${selectedUser.id}`;
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        await API.put(endpoint, updateData);
        setSuccess('User updated successfully');
      } else {
        // Create user
        const endpoint = isAdmin ? '/admin/users' : '/users';
        await API.post(endpoint, formData);
        setSuccess('User created successfully');
      }

      setModal(false);
      loadUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save user');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      role: '',
      manager_id: ''
    });
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      admin: 'bg-dark text-danger border border-danger',
      manager: 'bg-dark text-warning border border-warning',
      user: 'bg-dark text-success border border-success'
    };
    return classes[role] || 'bg-dark text-light border border-secondary';
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Crown size={14} className="me-1" />;
      case 'manager': return <Shield size={14} className="me-1" />;
      case 'user': return <User size={14} className="me-1" />;
      default: return <User size={14} className="me-1" />;
    }
  };

  const getStatusBadgeClass = (isActive) => {
    return isActive
      ? 'bg-dark text-success border border-success'
      : 'bg-dark text-danger border border-danger';
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2 className="page-heading">
            <UsersIcon className="page-heading-icon" />
            User Management
          </h2>
          <small className="text-muted">
            {isAdmin ? 'Manage all users and their roles' : 'Manage your users'}
          </small>
        </Col>
        <Col xs="auto">
          <Button color="primary" size="sm" onClick={handleCreate}>
            <Plus size={16} className="me-1" /> Add User
          </Button>
          <Button color="secondary" size="sm" className="ms-2" onClick={loadUsers}>
            <RefreshCw size={16} className="me-1" /> Refresh
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="bg-dark border-secondary mb-4">
        <div className="card-body">
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label className="text-white">Search</Label>
                <div className="position-relative">
                  <Input
                    type="text"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Search by name, email, username..."
                    className="bg-dark text-white ps-5"
                  />
                  <Search
                    size={16}
                    className="position-absolute top-50 translate-middle-y ms-3 text-muted"
                    style={{ left: '0.5rem' }}
                  />
                </div>
              </FormGroup>
            </Col>
            {isAdmin && (
              <>
                <Col md={3}>
                  <FormGroup>
                    <Label className="text-white">Role</Label>
                    <Input
                      type="select"
                      name="role"
                      value={filters.role}
                      onChange={handleFilterChange}
                      className="bg-dark text-white"
                    >
                      <option value="">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="user">User</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label className="text-white">Manager</Label>
                    <Input
                      type="select"
                      name="manager_id"
                      value={filters.manager_id}
                      onChange={handleFilterChange}
                      className="bg-dark text-white"
                    >
                      <option value="">All Managers</option>
                      {managers.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.company_name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </>
            )}
            <Col md={isAdmin ? 2 : 8} className="d-flex align-items-end">
              <Button color="outline-secondary" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </div>
      </Card>

      {/* Users Table */}
      <Card className="bg-dark border-secondary">
        <div className="card-body">
          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="text-muted">No users found</h5>
              <p className="text-muted">
                {Object.values(filters).some(v => v)
                  ? 'Try adjusting your search criteria'
                  : 'Click "Add User" to create your first user'
                }
              </p>
            </div>
          ) : (
            <>
              <Table dark hover responsive size="sm">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Contact</th>
                    {isAdmin && <th>Manager</th>}
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.first_name} {user.last_name}</strong>
                        <small className="d-block text-muted">
                          @{user.username}
                        </small>
                      </td>
                      <td>
                        <Badge className={getRoleBadgeClass(user.role)}>
                          {getRoleIcon(user.role)}
                          {user.role.toUpperCase()}
                        </Badge>
                      </td>
                      <td>
                        <div>{user.email}</div>
                        {user.phone && (
                          <small className="text-muted d-block">{user.phone}</small>
                        )}
                      </td>
                      {isAdmin && (
                        <td>
                          {user.manager ? (
                            <span>{user.manager.company_name}</span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      )}
                      <td>
                        <Badge className={getStatusBadgeClass(user.is_active)}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <Button
                            color="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            title="Edit User"
                          >
                            <Edit size={14} />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button
                              color="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(user.id)}
                              title="Delete User"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Row className="mt-3">
                  <Col className="text-center">
                    <span className="text-muted me-3">
                      Page {pagination.page} of {pagination.pages}
                      ({pagination.total} total users)
                    </span>
                    <div className="btn-group btn-group-sm">
                      <Button
                        color="outline-secondary"
                        disabled={!pagination.has_prev}
                        onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      >
                        Previous
                      </Button>
                      <Button
                        color="outline-secondary"
                        disabled={!pagination.has_next}
                        onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      >
                        Next
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}
        </div>
      </Card>

      {/* User Form Modal */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg" className="modal-dark">
        <ModalHeader toggle={() => setModal(false)}>
          {editMode ? 'Edit User' : 'Add New User'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {error && <Alert color="danger">{error}</Alert>}

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">Username *</Label>
                  <Input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">First Name</Label>
                  <Input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">Last Name</Label>
                  <Input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">Phone</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">Company</Label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="bg-dark text-white"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-white">
                    {editMode ? 'New Password' : 'Password *'}
                  </Label>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editMode}
                    className="bg-dark text-white"
                    placeholder={editMode ? 'Leave empty to keep current password' : ''}
                  />
                  {!editMode && (
                    <small className="text-muted">Minimum 8 characters</small>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                {isAdmin && (
                  <FormGroup>
                    <Label className="text-white">Role</Label>
                    <Input
                      type="select"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="bg-dark text-white"
                    >
                      <option value="user">User</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </Input>
                  </FormGroup>
                )}
              </Col>
            </Row>

            {isAdmin && formData.role === 'user' && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="text-white">Manager</Label>
                    <Input
                      type="select"
                      name="manager_id"
                      value={formData.manager_id}
                      onChange={handleInputChange}
                      className="bg-dark text-white"
                    >
                      <option value="">No Manager</option>
                      {managers.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.company_name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6} className="d-flex align-items-center">
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <Label check className="text-white">
                      Active Account
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="primary">
              {editMode ? 'Update User' : 'Create User'}
            </Button>
            <Button color="secondary" onClick={() => setModal(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </Container>
  );
};

export default Users;
